// Phase A.4 / 8C.6 — Readiness gate card rendered inline in chat.
// Upgraded to use SIKA design tokens with premium plan-card layout.

import { Icon } from "../../../components/icons/Icon.tsx";

interface Blocker {
  code?: string;
  message?: string;
}

interface ReadinessData {
  ok?: boolean;
  ready?: boolean;
  blockers?: Blocker[];
  gates?: Array<{ gate?: string; label?: string; passed?: boolean; detail?: string }>;
  goal_id?: string;
  chain_id?: string;
  error?: string;
}

const GATE_ICONS: Record<string, string> = {
  no_bankroll: "wallet",
  separation: "shield",
  drawdown: "alert",
  sub_ratio: "chart",
  goal_missing: "target",
  goal_status: "target",
  chain_missing: "link",
  chain_status: "link",
  not_linked: "link",
};

export function SikaReadinessCard({ data }: { data: unknown }): JSX.Element {
  const d = (data ?? {}) as ReadinessData;

  if (d.error) {
    return (
      <div className="plan-card" style={{ borderColor: "var(--sika-negative, #C0392B)" }}>
        <div className="plan-head">
          <div className="label">Readiness Check</div>
          <div className="ttl" style={{ color: "var(--sika-negative, #C0392B)" }}>Error</div>
          <div className="meta">{d.error}</div>
        </div>
      </div>
    );
  }

  // New format: ok + blockers (from check_readiness tool)
  if ("ok" in d) {
    const allPassed = d.ok === true;
    const blockers = d.blockers ?? [];

    return (
      <div className="plan-card" style={{
        borderColor: allPassed ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
      }}>
        <div className="plan-head">
          <div className="label">Activation Readiness</div>
          <div className="ttl" style={{
            fontFamily: "var(--sika-font-sans)",
            fontSize: "var(--sika-text-lg)",
            color: allPassed ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
          }}>
            {allPassed ? "Ready to Activate" : "Not Ready"}
          </div>
          <div className="meta">
            {allPassed
              ? "All gates passed"
              : `${blockers.length} blocker${blockers.length !== 1 ? "s" : ""} remaining`}
          </div>
        </div>

        {allPassed ? (
          <div className="plan-stats">
            <div className="stat-row">
              <span className="lbl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name="check" size={14} color="var(--sika-positive, #27AE60)" />
                All activation prerequisites met
              </span>
            </div>
          </div>
        ) : (
          <div className="honest-assessment">
            <div className="assess-label">Blockers</div>
            {blockers.map((b, i) => (
              <div key={b.code ?? i} className="assess-row">
                <span className="lbl" style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <Icon
                    name={GATE_ICONS[b.code ?? ""] ?? "close"}
                    size={12}
                    color="var(--sika-negative, #C0392B)"
                  />
                  {b.message ?? b.code ?? "Unknown blocker"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Legacy format: gates array
  const gates = d.gates ?? [];
  const allPassed = d.ready ?? gates.every((g) => g.passed);

  return (
    <div className="plan-card" style={{
      borderColor: allPassed ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
    }}>
      <div className="plan-head">
        <div className="label">Activation Readiness</div>
        <div className="ttl" style={{
          fontFamily: "var(--sika-font-sans)",
          fontSize: "var(--sika-text-lg)",
          color: allPassed ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
        }}>
          {allPassed ? "Ready to Activate" : "Not Ready"}
        </div>
        <div className="meta">
          {gates.filter((g) => g.passed).length}/{gates.length} gates passed
        </div>
      </div>
      <div className="plan-stats">
        {gates.map((g, i) => (
          <div key={g.gate ?? i} className="stat-row">
            <span className="lbl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon
                name={g.passed ? "check" : "close"}
                size={12}
                color={g.passed ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)"}
              />
              {g.label ?? g.gate ?? `Gate ${String(i + 1)}`}
            </span>
            {g.detail && (
              <span className="val" style={{ fontSize: "var(--sika-text-xs)", color: "var(--sika-text-muted)" }}>
                {g.detail}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
